// engine/biometrics.ts — inverted biometrics (SPEC §6). All local, never uploaded.
//
// The premise of the whole act: a machine is *too good* at these. Humans pass by being
// imperfect — timing that varies, a circle that wobbles, a hand that will not hold still.
// Perfection is the failure state. This module owns the three measurements:
//   · keystroke variance      (L04) — record inter-key intervals, compare two attempts
//   · circularity             (L05) — least-squares circle fit → a 0–100 score
//   · tremor                  (L07) — 60Hz pointer sampling → path length + live waveform
//
// The pure math (compareTimings / circularity / tremorStats) has NO DOM dependency so it
// can be re-derived and unit-tested off-DOM; the DOM recorders/samplers wrap it. Nothing
// here ever transmits: the levels return only verdicts + traits, and (L05) a score number.

export interface Point {
  x: number;
  y: number;
}

// ── Keystroke variance (L04) ───────────────────────────────────────────
// SPEC §6: flag if inter-key correlation > 0.985 AND mean-diff < 8ms, or a paste event.
// These thresholds are deliberately unreachable for a real hand: human test-retest
// keystroke correlation sits well under 0.9, and a sub-8ms mean interval match across a
// whole word does not happen without a machine (or a paste, handled separately).

export const MIN_INTERVALS = 3; // fewer than this and there is nothing to judge → pass
const IDENTICAL_R = 0.985;
const IDENTICAL_MEAN_DIFF_MS = 8;

/** Records inter-key timing for one attempt on a text field, and whether it was pasted. */
export class KeystrokeRecorder {
  private stamps: number[] = [];
  private _pasted = false;

  private readonly onKey = (e: KeyboardEvent) => {
    // Only printable, unmodified keystrokes carry rhythm. Navigation/backspace/modifiers
    // would desync the two attempts' alignment and are not what we are measuring.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      this.stamps.push(performance.now());
    }
  };
  private readonly onPaste = () => {
    this._pasted = true;
  };

  constructor(private readonly input: HTMLElement) {
    input.addEventListener("keydown", this.onKey);
    input.addEventListener("paste", this.onPaste);
  }

  get pasted(): boolean {
    return this._pasted;
  }

  /** Inter-key intervals (ms) for this attempt: gaps between consecutive keystrokes. */
  intervals(): number[] {
    const out: number[] = [];
    for (let i = 1; i < this.stamps.length; i++) out.push(this.stamps[i]! - this.stamps[i - 1]!);
    return out;
  }

  reset(): void {
    this.stamps = [];
    this._pasted = false;
  }

  detach(): void {
    this.input.removeEventListener("keydown", this.onKey);
    this.input.removeEventListener("paste", this.onPaste);
  }
}

export interface TimingComparison {
  /** Pearson correlation of the two interval series (1 for identical constant rhythm). */
  correlation: number;
  /** Mean absolute difference between aligned intervals, ms. */
  meanDiff: number;
  /** True only when BOTH thresholds trip — implausibly identical, machine-like. */
  tooIdentical: boolean;
  /** False when the attempts are not alignable (different length / too short) → treat as pass. */
  comparable: boolean;
}

/** Compare two keystroke attempts. Only equal-length, long-enough series are judged. */
export function compareTimings(a: number[], b: number[]): TimingComparison {
  const comparable = a.length === b.length && a.length >= MIN_INTERVALS;
  if (!comparable) {
    return { correlation: 0, meanDiff: Infinity, tooIdentical: false, comparable: false };
  }
  const n = a.length;
  let sumAbs = 0;
  for (let i = 0; i < n; i++) sumAbs += Math.abs(a[i]! - b[i]!);
  const meanDiff = sumAbs / n;

  const r = pearson(a, b);
  let tooIdentical = false;
  if (meanDiff < IDENTICAL_MEAN_DIFF_MS) {
    // A metronome (constant intervals) yields zero variance and NaN correlation — that is
    // the most machine-like case of all, so a near-zero mean-diff there is itself a flag.
    tooIdentical = Number.isNaN(r) ? true : r > IDENTICAL_R;
  }
  return { correlation: Number.isNaN(r) ? 1 : r, meanDiff, tooIdentical, comparable: true };
}

function pearson(a: number[], b: number[]): number {
  const n = a.length;
  let ma = 0;
  let mb = 0;
  for (let i = 0; i < n; i++) {
    ma += a[i]!;
    mb += b[i]!;
  }
  ma /= n;
  mb /= n;
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    const xa = a[i]! - ma;
    const xb = b[i]! - mb;
    num += xa * xb;
    da += xa * xa;
    db += xb * xb;
  }
  if (da === 0 || db === 0) return NaN; // one series is constant (metronome)
  return num / Math.sqrt(da * db);
}

// ── Circularity (L05) ──────────────────────────────────────────────────
// Algebraic (Kåsa) least-squares circle fit: minimize Σ((xᵢ−a)²+(yᵢ−b)²−R²)². The
// linearized normal equations solve for centre and radius in one 3×3 system. Score is
// 1 − RMS radial deviation / radius, so a compass gets ~100% and a shaky hand gets less.

const MIN_POINTS = 8;
const MIN_RADIUS = 8; // px — a scribble smaller than this is not a circle attempt
// A straight line least-squares-fits to a huge far-off circle with tiny relative RMS —
// which would score ~100%. Requiring the path to actually wrap the centre kills that.
const COVERAGE_MIN = 5.0; // rad (~286°): below this it is an arc, not a loop

export interface CircleFit {
  cx: number;
  cy: number;
  r: number;
  /** RMS radial deviation of the samples from the fitted circle, px. */
  rms: number;
}

export interface CircleScore {
  /** 0–100, one decimal (SPEC shows "97.4%"). */
  score: number;
  fit: CircleFit;
  /** Total angular sweep around the centre, rad — how much of a loop was drawn. */
  coverage: number;
}

export function fitCircle(pts: Point[]): CircleFit | null {
  const n = pts.length;
  if (n < MIN_POINTS) return null;

  let Sx = 0;
  let Sy = 0;
  let Sxx = 0;
  let Syy = 0;
  let Sxy = 0;
  let Sxz = 0;
  let Syz = 0;
  let Sz = 0;
  for (const p of pts) {
    const z = p.x * p.x + p.y * p.y;
    Sx += p.x;
    Sy += p.y;
    Sxx += p.x * p.x;
    Syy += p.y * p.y;
    Sxy += p.x * p.y;
    Sxz += p.x * z;
    Syz += p.y * z;
    Sz += z;
  }
  // M·c = v where the circle is x²+y² = c₁x + c₂y + c₃, centre (c₁/2, c₂/2).
  const c = solve3(
    [
      [Sxx, Sxy, Sx],
      [Sxy, Syy, Sy],
      [Sx, Sy, n],
    ],
    [Sxz, Syz, Sz],
  );
  if (!c) return null;
  const cx = c[0] / 2;
  const cy = c[1] / 2;
  const rsq = c[2] + cx * cx + cy * cy;
  if (!(rsq > 0)) return null;
  const r = Math.sqrt(rsq);

  let se = 0;
  for (const p of pts) {
    const d = Math.hypot(p.x - cx, p.y - cy) - r;
    se += d * d;
  }
  return { cx, cy, r, rms: Math.sqrt(se / n) };
}

function det3(m: number[][]): number {
  return (
    m[0]![0]! * (m[1]![1]! * m[2]![2]! - m[1]![2]! * m[2]![1]!) -
    m[0]![1]! * (m[1]![0]! * m[2]![2]! - m[1]![2]! * m[2]![0]!) +
    m[0]![2]! * (m[1]![0]! * m[2]![1]! - m[1]![1]! * m[2]![0]!)
  );
}

/** Solve the 3×3 system M·c = v by Cramer's rule; null if singular. */
function solve3(M: number[][], v: number[]): [number, number, number] | null {
  const d = det3(M);
  if (Math.abs(d) < 1e-9) return null;
  const withCol = (i: number): number[][] => {
    const m = M.map((row) => row.slice());
    for (let k = 0; k < 3; k++) m[k]![i] = v[k]!;
    return m;
  };
  return [det3(withCol(0)) / d, det3(withCol(1)) / d, det3(withCol(2)) / d];
}

/** Total unwrapped angular sweep of the path around (cx, cy). Full loop ≈ 2π. */
export function angularCoverage(pts: Point[], cx: number, cy: number): number {
  if (pts.length < 2) return 0;
  let total = 0;
  let prev = Math.atan2(pts[0]!.y - cy, pts[0]!.x - cx);
  for (let i = 1; i < pts.length; i++) {
    const a = Math.atan2(pts[i]!.y - cy, pts[i]!.x - cx);
    let d = a - prev;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    total += d;
    prev = a;
  }
  return Math.abs(total);
}

/** Score a drawn ring 0–100, or null if it is not enough of a loop to judge (→ redraw). */
export function circularity(pts: Point[]): CircleScore | null {
  const fit = fitCircle(pts);
  if (!fit || fit.r < MIN_RADIUS) return null;
  const coverage = angularCoverage(pts, fit.cx, fit.cy);
  if (coverage < COVERAGE_MIN) return null;
  const raw = 1 - fit.rms / fit.r;
  const score = Math.max(0, Math.min(100, Math.round(raw * 1000) / 10));
  return { score, fit, coverage };
}

/** SPEC §3/§6 thresholds for the L05 verdict branches. */
export const CIRCLE_TOO_PERFECT = 97;
export const CIRCLE_PRECISE = 90;
export const CIRCLE_FLAWED = 70;

// ── Tremor (L07) ───────────────────────────────────────────────────────
// SPEC §6: 60Hz pointer sampling over 10s; total path length < 3px → strike (a machine,
// or a stone, holds perfectly still). A human hand always drifts. The live waveform is
// both proof and a screenshot magnet.

export const ZERO_DRIFT_PATH = 3; // px total over the window → strike
export const TREMBLE_PATH = 60; // px total → "trembling" rather than "steady"

export interface TremorStats {
  /** Sum of consecutive sample distances, px — the "how much did the hand move" number. */
  pathLength: number;
  /** Std dev of sample positions about their centroid, px. */
  stdDev: number;
  samples: number;
}

export function tremorStats(pts: Point[]): TremorStats {
  const n = pts.length;
  if (n === 0) return { pathLength: 0, stdDev: 0, samples: 0 };
  let path = 0;
  for (let i = 1; i < n; i++) {
    path += Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.y - pts[i - 1]!.y);
  }
  let mx = 0;
  let my = 0;
  for (const p of pts) {
    mx += p.x;
    my += p.y;
  }
  mx /= n;
  my /= n;
  let v = 0;
  for (const p of pts) v += (p.x - mx) ** 2 + (p.y - my) ** 2;
  return { pathLength: path, stdDev: Math.sqrt(v / n), samples: n };
}

/**
 * 60Hz pointer sampler with a live seismograph-style waveform (SPEC §6, L07).
 * The canvas is both the sensor (pointer target) and the display. Sampling holds the last
 * known pointer position each animation frame, so perfect stillness records a flat line —
 * which is exactly the failure the level is looking for.
 */
export class TremorSampler {
  private readonly samples: Point[] = [];
  private last: Point | null = null;
  private raf = 0;
  private running = false;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly cssW: number;
  private readonly cssH: number;

  private readonly onMove = (e: PointerEvent) => {
    const rect = this.canvas.getBoundingClientRect();
    this.last = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("tremor: no 2d context");
    this.ctx = ctx;
    this.cssW = canvas.clientWidth || canvas.width;
    this.cssH = canvas.clientHeight || canvas.height;
  }

  /** Begin sampling from an initial position (the pointerdown point). */
  begin(origin: Point): void {
    this.samples.length = 0;
    this.last = origin;
    this.running = true;
    this.canvas.addEventListener("pointermove", this.onMove);
    const loop = () => {
      if (!this.running) return;
      if (this.last) this.samples.push(this.last);
      this.draw();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  /** Stop sampling and return the captured path. */
  stop(): Point[] {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointermove", this.onMove);
    return this.samples.slice();
  }

  private draw(): void {
    const { ctx, cssW: w, cssH: h } = this;
    ctx.clearRect(0, 0, w, h);

    const mid = h / 2;
    const style = getComputedStyle(this.canvas);
    const accent = style.getPropertyValue("--accent").trim() || "#E8A13C";
    const line = style.getPropertyValue("--line").trim() || "#23252B";

    // baseline
    ctx.strokeStyle = line;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(w, mid);
    ctx.stroke();

    if (this.samples.length < 2) return;
    const origin = this.samples[0]!;
    const GAIN = 6; // amplify micro-tremor so it is visible
    const N = this.samples.length;
    // Show the most recent window scrolling across the pad.
    const shown = Math.min(N, 600);
    const startIdx = N - shown;

    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.beginPath();
    for (let i = 0; i < shown; i++) {
      const s = this.samples[startIdx + i]!;
      const px = (i / (shown - 1)) * w;
      // vertical drift, amplified and clamped inside the pad
      const dy = (s.y - origin.y) * GAIN;
      const py = Math.max(4, Math.min(h - 4, mid - dy));
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
}
